import React from 'react';
import { BreadcrumbSchema, FAQSchema, ArticleSchema } from '@/components/JsonLd';
import {
  RichHero, TableOfContents, ProseSection, AnswerBox, FactGrid,
  ComparisonTable, ProsCons, RichFAQ, RelatedLinks, RichCTA, ChargerCard,
} from '../RichBlocks';
import { ENKEL_PRODUCTS } from '@/components/seo/content/ContentRegistry';
import SEODisclaimer from '@/components/seo/content/SEODisclaimer';

/**
 * NexBlue Edge 2 vs Zaptec Go — bespoke rich comparison. Geo-aware.
 * Registered for base + regional variants. Both sold by enkel.
 * Sources: NexBlue + Zaptec official product specs.
 */
export default function NexBlueVsZaptecGo({ page }) {
  const geo = page?.geography;
  const place = geo?.name ? ` i ${geo.name}` : '';
  const zone = geo?.zone;
  const zoneLabel = zone === 'DK1' ? 'DK1 (Vestdanmark)' : zone === 'DK2' ? 'DK2 (Østdanmark)' : null;

  const nex = ENKEL_PRODUCTS.chargers.nexblue_edge_2;
  const go = ENKEL_PRODUCTS.chargers.zaptec_go;

  const updated = new Date(page?.last_data_refresh || page?.updated_date || Date.now())
    .toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' });

  const faqs = [
    {
      question: 'NexBlue Edge 2 eller Zaptec Go – hvad skal jeg vælge?',
      answer: `Begge er fremragende 22 kW Type 2-ladebokse. NexBlue Edge 2 (${nex.price.toLocaleString('da-DK')} kr.) er bedst til prisen og er V2G-certificeret med dansk design. Zaptec Go (${go.price.toLocaleString('da-DK')} kr.) er Nordens mest solgte og kendt for sin driftssikkerhed. Vælg NexBlue, hvis pris og fremtidssikring vejer tungest; vælg Zaptec Go, hvis du vil have et velkendt mærke med dokumenteret stabilitet.`,
    },
    {
      question: 'Er der forskel på ladehastigheden?',
      answer: 'Nej. Begge leverer op til 22 kW på en 3-faset installation. Den faktiske hastighed afhænger af din bil og din elinstallation – ikke af mærket.',
    },
    {
      question: 'Er NexBlue Edge 2 V2G-klar?',
      answer: 'Ja. NexBlue Edge 2 er forberedt til V2X/V2G, så bilen på sigt kan sende strøm tilbage, når lovgivning og bil understøtter det. Zaptec Go er ikke V2G-ready – det er derimod Zaptec Go 2.',
    },
    {
      question: 'Hvad er inkluderet hos enkel?',
      answer: 'Begge bokse leveres med Saving Mode, serviceaftale og 10 øre/kWh laderabat til 0 kr./md., når du har el-aftale hos enkel energi. Installation kommer oveni fra 1.300 kr.',
    },
    {
      question: 'Passer begge til min elbil?',
      answer: 'Ja. Begge bruger Type 2-stik, som er standarden for alle moderne elbiler i Europa – Tesla, VW, Kia, Hyundai, Polestar m.fl.',
    },
  ];

  const toc = [
    { id: 'svar', label: 'Kort svar' },
    { id: 'tabel', label: 'Side-om-side' },
    { id: 'nexblue', label: 'NexBlue Edge 2' },
    { id: 'zaptec', label: 'Zaptec Go' },
    { id: 'anbefaling', label: 'Anbefaling' },
    { id: 'faq', label: 'FAQ' },
  ];

  return (
    <article itemScope itemType="https://schema.org/Article">
      <BreadcrumbSchema items={[{ name: 'Forside', path: '/' }, { name: 'Ladebokse', path: '/opladere' }, { name: `NexBlue Edge 2 vs Zaptec Go${place}`, path: `/sammenlign/${page.slug}` }]} />
      <FAQSchema faqs={faqs} />
      <ArticleSchema headline={`NexBlue Edge 2 vs Zaptec Go${place}`} description={page?.meta_description} datePublished={page?.created_date} dateModified={page?.last_data_refresh || page?.updated_date} />

      <RichHero
        eyebrow={`Sammenligning · Ladebokse${zoneLabel ? ' · ' + zone : ''} · 2026`}
        h1={`NexBlue Edge 2 vs Zaptec Go${place}`}
        intro={`To af de bedste hjemmeladere på det danske marked – begge solgt af enkel energi. Vi sammenligner pris, V2G og features${place}, så du kan vælge den rigtige.`}
        badges={['Begge 22 kW', 'Type 2-stik', 'Saving Mode inkl.']}
        updated={updated}
        image="https://images.unsplash.com/photo-1647500666543-2f5f1e7e0b6e?auto=format&fit=crop&w=1600&q=70"
        kpis={[
          { value: `${nex.price.toLocaleString('da-DK')} kr.`, label: 'NexBlue Edge 2 (V2G)' },
          { value: `${go.price.toLocaleString('da-DK')} kr.`, label: 'Zaptec Go' },
          { value: '22 kW', label: 'begge modeller' },
          { value: '0 kr./md.', label: 'Saving Mode + service' },
        ]}
      />
      <TableOfContents items={toc} />

      <ProseSection id="svar" heading="NexBlue eller Zaptec Go – kort svar">
        <AnswerBox question="Hvilken skal jeg vælge?">
          <strong>NexBlue Edge 2</strong> ({nex.price.toLocaleString('da-DK')} kr.) er <strong>bedst til prisen</strong> og V2G-certificeret med
          dansk design. <strong>Zaptec Go</strong> ({go.price.toLocaleString('da-DK')} kr.) er <strong>Nordens mest solgte</strong> med et stærkt ry
          for driftssikkerhed. Begge lader med 22 kW og inkluderer Saving Mode, serviceaftale og laderabat til 0 kr./md.
        </AnswerBox>
        {zoneLabel && (
          <p>
            {geo.name} ligger i <strong>{zoneLabel}</strong>. Uanset valg flytter enkel Saving Mode automatisk din opladning til de billigste
            timer på Nord Pool i {zone}.
          </p>
        )}
      </ProseSection>

      <ProseSection id="tabel" heading="NexBlue Edge 2 vs Zaptec Go – side om side" alt>
        <div className="my-2">
          <ComparisonTable
            caption="Sammenligning af NexBlue Edge 2 og Zaptec Go"
            columns={[{ name: 'NexBlue Edge 2', highlight: true }, { name: 'Zaptec Go' }]}
            rows={[
              { label: 'Pris', values: [`${nex.price.toLocaleString('da-DK')} kr.`, `${go.price.toLocaleString('da-DK')} kr.`] },
              { label: 'Producent', values: ['NexBlue (DK)', 'Zaptec (NO)'] },
              { label: 'Max effekt', values: ['22 kW', '22 kW'] },
              { label: 'Stiktype', values: ['Type 2', 'Type 2'] },
              { label: 'Forbindelse', values: ['Wi-Fi + 4G', 'Wi-Fi + 4G'] },
              { label: 'Load balancing', values: [true, true] },
              { label: 'V2X / V2G-ready', values: [true, false] },
              { label: 'IP65 vejrbestandig', values: [true, true] },
              { label: 'Saving Mode', values: [true, true] },
              { label: 'Serviceaftale', values: ['0 kr./md.', '0 kr./md.'] },
            ]}
          />
        </div>
        <p className="text-xs text-gray-500 mt-3">Kilder: NexBlue og Zaptec officielle produktspecifikationer.</p>
      </ProseSection>

      <div className="container mx-auto px-6 py-10">
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          <div id="nexblue">
            <ChargerCard charger={nex} note="Bedst til prisen · V2G" />
            <div className="mt-4">
              <ProsCons
                pros={['Laveste pris', 'V2X/V2G-certificeret', 'Dansk design', 'Saving Mode + service inkl.']}
                cons={['Nyere mærke end Zaptec', 'Ingen indbygget skærm']}
              />
            </div>
          </div>
          <div id="zaptec">
            <ChargerCard charger={go} note="Nordens mest solgte" />
            <div className="mt-4">
              <ProsCons
                pros={['Bevist pålidelig', 'Stærk load balancing', 'Velkendt mærke', 'Saving Mode + service inkl.']}
                cons={['Højere pris end NexBlue', 'Ikke V2G-ready (det er Go 2)']}
              />
            </div>
          </div>
        </div>
      </div>

      <ProseSection id="anbefaling" heading="Vores anbefaling" alt>
        <FactGrid columns={2} facts={[
          { value: 'Vælg NexBlue', label: 'Hvis pris + fremtidssikring vejer tungest', sub: 'V2G-certificeret til laveste pris' },
          { value: 'Vælg Zaptec Go', label: 'Hvis du vil have det mest gennemtestede', sub: 'Nordens mest solgte' },
        ]} />
        <p className="mt-6">
          Begge er fremragende valg. NexBlue Edge 2 giver mest teknologi for pengene og er fremtidssikret med V2G, mens Zaptec Go er det trygge,
          gennemtestede valg. Uanset hvad får du Saving Mode, serviceaftale og laderabat til 0 kr./md. og spotpris uden tillæg fra 18 kr./md.
        </p>
      </ProseSection>

      <section className="py-12 bg-[#F6F3EC]">
        <div className="container mx-auto px-6">
          <h2 id="faq" className="text-2xl md:text-3xl font-bold text-center mb-8 text-[#1F2E33]" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>Ofte stillede spørgsmål</h2>
          <RichFAQ faqs={faqs} />
        </div>
      </section>

      <RelatedLinks links={[
        { path: '/nexblue-edge-2', label: 'NexBlue Edge 2 – produktside' },
        { path: '/zaptec-go', label: 'Zaptec Go – produktside' },
        { path: '/sammenlign/zaptec-go-vs-go-2', label: 'Zaptec Go vs Go 2' },
        { path: '/hvilken-ladeboks-skal-jeg-vaelge', label: 'Hvilken ladeboks skal jeg vælge?' },
        { path: '/opladere', label: 'Alle ladebokse' },
        { path: '/elaftale', label: 'Se el-aftaler' },
      ]} />

      <RichCTA
        headline="Vælg din ladeboks"
        subtext="NexBlue Edge 2 og Zaptec Go inkluderer begge Saving Mode, serviceaftale og laderabat til 0 kr./md."
        primary={{ path: '/opladere', text: 'Se ladebokse' }}
        secondary={{ path: '/hvilken-ladeboks-skal-jeg-vaelge', text: 'Køberguide' }}
      />
      <SEODisclaimer page={page} />
    </article>
  );
}